<script setup lang="ts">
import { computed } from 'vue'

interface Props {
	/** Diameter in px */
	size?: number
	/** Optional Vuetify theme token; tints both rings (default: brand secondary sweep + primary core) */
	color?: string
}

const props = withDefaults(defineProps<Props>(), {
	size: 48,
	color: undefined,
})

const colorOverride = computed(() =>
	props.color
		? {
				'--radar-ring': `rgb(var(--v-theme-${props.color}))`,
				'--radar-core': `rgb(var(--v-theme-${props.color}))`,
			}
		: undefined,
)
</script>

<template>
	<svg
		class="radar-spinner"
		:width="size"
		:height="size"
		:style="colorOverride"
		viewBox="0 0 48 48"
		aria-hidden="true"
	>
		<circle
			class="radar-spinner__track"
			cx="24"
			cy="24"
			r="20"
		/>
		<circle
			class="radar-spinner__sweep"
			cx="24"
			cy="24"
			r="20"
		/>
		<circle
			class="radar-spinner__pulse"
			cx="24"
			cy="24"
			r="11"
		/>
		<circle
			class="radar-spinner__blip"
			cx="24"
			cy="24"
			r="4"
		/>
		<circle
			class="radar-spinner__core"
			cx="24"
			cy="24"
			r="3.2"
		/>
	</svg>
</template>

<style scoped lang="scss">
// "Prospecting radar" brand loader: rotating sweep arc (secondary), pulsing
// inner ring (primary) and a radar-contact blip in the center. Keyframes come
// from the global library (styles/_animations.scss); the global
// prefers-reduced-motion guard freezes it into a static mark.
.radar-spinner {
	--radar-ring: rgb(var(--v-theme-secondary));
	--radar-core: rgb(var(--v-theme-primary));

	&__track {
		fill: none;
		stroke: var(--radar-ring);
		stroke-opacity: 0.18;
		stroke-width: 2.5;
	}

	&__sweep {
		fill: none;
		stroke: var(--radar-ring);
		stroke-width: 2.5;
		stroke-linecap: round;
		stroke-dasharray: 31.4 94.2; // quarter arc of the r=20 circumference
		transform-origin: center;
		animation: orbit 1.4s linear infinite;
	}

	&__pulse {
		fill: none;
		stroke: var(--radar-core);
		stroke-opacity: 0.4;
		stroke-width: 2;
		transform-origin: center;
		animation: pulse-soft 2s $ease-standard infinite;
	}

	&__blip {
		fill: var(--radar-ring);
		transform-origin: center;
		animation: radar-blip 1.4s $ease-standard infinite;
	}

	&__core {
		fill: var(--radar-core);
	}
}
</style>
